import {useMemo} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartFeatures,
  isFlowchartFeatureEnabled,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessFlowchartEditorModeFeatureType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';

import {getAdapterForTaskSchemaNode} from './adapterUtils';
import {FlowchartNode} from './processFlowchartConfigEditorNodeTypes';

interface UseSelectedNodeInfoParams {
  interactiveNodes: Array<FlowchartNode>;
  flowchartFeatures: FlowchartFeatures;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  locked: boolean;
}

interface UseSelectedNodeInfoReturn {
  selectedNode: FlowchartNode | null;
  selectedFieldName: string | null;
  adapterForSelectedNode: ReturnType<typeof getAdapterForTaskSchemaNode>;
  metadataSidepanel:
    | ReturnType<typeof PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType>['metadataEditor']
    | undefined;
  hasLeftSidebarContent: boolean;
}

/**
 * Derives information about the currently selected node and field from the sidepanel state,
 * including the node's adapter, resolved field name, metadata sidepanel config, and whether
 * the left sidebar has content to display.
 */
export default function useSelectedNodeInfo({
  interactiveNodes,
  flowchartFeatures,
  flowchartEditorConfigurator,
  locked,
}: UseSelectedNodeInfoParams): UseSelectedNodeInfoReturn {
  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const {nodeId: selectedNodeId, fieldId: selectedFieldId} = sidepanelManager.getEffectiveState();

  const selectedNode = useMemo(
    () => interactiveNodes.find((n) => n.id === selectedNodeId) ?? null,
    [interactiveNodes, selectedNodeId]
  );

  const adapterForSelectedNode = getAdapterForTaskSchemaNode(selectedNode);

  const selectedFieldName = useMemo(() => {
    if (!selectedFieldId || !selectedNode) {
      return null;
    }

    const fieldName = adapterForSelectedNode?.getFieldNameFromFieldId(selectedNode, selectedFieldId);
    if (fieldName == null) {
      // If field was removed, close the sidepanel (ex: removed map from start field from node in canvas)
      sidepanelManager.forceClose();
      return null;
    }
    return fieldName;
  }, [selectedFieldId, selectedNode, adapterForSelectedNode, sidepanelManager]);

  const metadataSidepanel = useMemo(
    () =>
      isFlowchartFeatureEnabled(
        flowchartFeatures,
        ProcessFlowchartEditorModeFeatureType.SHOW_FLOWCHART_SETTINGS_IF_DEFINED
      )
        ? PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(flowchartEditorConfigurator.flowchartTypeName)
            .metadataEditor
        : undefined,
    [flowchartFeatures, flowchartEditorConfigurator.flowchartTypeName]
  );

  const hasLeftSidebarContent = useMemo(() => {
    if (
      !locked &&
      isFlowchartFeatureEnabled(
        flowchartFeatures,
        ProcessFlowchartEditorModeFeatureType.ALLOW_NODE_ADD_DELETE
      ) &&
      flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL)
    ) {
      return true;
    }
    if (metadataSidepanel != null) {
      return true;
    }
    if (
      flowchartEditorConfigurator.hasFeature(
        ProcessFlowchartPlatformFeatureType.SHOW_FLOWCHART_TRANSITIONS_SIDEPANEL
      )
    ) {
      return true;
    }
    return false;
  }, [locked, flowchartFeatures, flowchartEditorConfigurator, metadataSidepanel]);

  return {
    selectedNode,
    selectedFieldName,
    adapterForSelectedNode,
    metadataSidepanel,
    hasLeftSidebarContent,
  };
}
